/**
 * Resource configuration for Projects Portfolio
 * Maps facetKeys to Typesense fields and defines value sources
 *
 * Feature-based visibility:
 * - Facets can be conditionally shown/hidden based on user's enabled features
 * - Use the `featureFlag` property in facet config to specify required feature
 *   (TLP features - not Posthog flags)
 * - AddFilterMenu component automatically filters facets based on user's enabled features
 * - Features are fetched via React Query and cached across all components
 */

import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import i18n from 'i18next';

import {
  employeeHoverCardFields,
  customerHoverCardFields,
  projectFields,
} from './helpers';
import { ConfigurationFeaturesResponse } from '@/api/REST/configuration-features';
import { TablePreferenceStorageKeys } from '@/hooks/grid/useTablePreferences';
import type { ResourceConfig } from '@/lib/resource-config';
import {
  HEALTH_STATUSES,
  PROJECT_STATUSES,
} from '@/pages/projects/portfolio/constants';

export const projectsConfig = {
  pageIdentifier: 'ProjectPortfolioOverview',
  resourceKey: 'portfolio', // Matches i18n namespace
  typesenseCollection: 'projects',
  searchFields: ['Name', 'Number', 'ProjectManagerName', 'PoNumber'],
  facetSections: {
    // For future reference when we need more sections
    // general: {
    //   get label() {
    //     return i18n.t('facetSections.General', { ns: 'portfolio' });
    //   },
    // },
    project: {
      get label() {
        return i18n.t('facetSections.Project', { ns: 'portfolio' });
      },
    },
  },
  facets: {
    project: {
      section: 'project',
      get label() {
        return i18n.t('facets.Project', { ns: 'portfolio' });
      },
      field: 'SourceId', // Filter by project's own ID
      facetField: 'Name', // Readable counts
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'projects', // Self-referential
        idField: 'SourceId',
        slugField: 'SourceId',
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      hoverCard: projectFields,
    },

    customer: {
      section: 'project',
      get label() {
        return i18n.t('facets.Customer', { ns: 'portfolio' });
      },
      field: 'CustomerId',
      facetField: 'CustomerName', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'customers',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true, // Now we can enable faceting with readable names
      includeNullInNot: true,
      hoverCard: customerHoverCardFields,
    },

    ['project-manager']: {
      section: 'project',
      get label() {
        return i18n.t('facets.ProjectManager', { ns: 'portfolio' });
      },
      field: 'ProjectManagerId',
      facetField: 'ProjectManagerName', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'employees',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
        apiKeyName: 'projectmanagers', // Use project managers API key instead of employees key
      },
      facetBy: true,
      includeNullInNot: true,
      hoverCard: employeeHoverCardFields,
    },

    ['account-manager']: {
      section: 'project',
      get label() {
        return i18n.t('facets.AccountManager', { ns: 'portfolio' });
      },
      field: 'AccountManagerId',
      facetField: 'AccountManagerName', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'employees',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
        apiKeyName: 'accountmanagers', // Use account managers API key instead of employees key
      },
      facetBy: true,
      includeNullInNot: true,
      hoverCard: employeeHoverCardFields,
    },

    partner: {
      section: 'project',
      get label() {
        return i18n.t('facets.Partner', { ns: 'portfolio' });
      },
      field: 'PartnerId',
      facetField: 'PartnerName', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'employees',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
        apiKeyName: 'partners', // Use partners API key instead of employees key
      },
      facetBy: true,
      includeNullInNot: true,
      featureFlag: 'partner',
      hoverCard: employeeHoverCardFields,
    },

    ['legal-entities']: {
      section: 'project',
      get label() {
        return i18n.t('facets.LegalEntity', { ns: 'portfolio' });
      },
      field: 'LegalEntityId',
      facetField: 'LegalEntityName', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'legalentities',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      includeNullInNot: true,
      featureFlag: 'multiLegalEntities',
    },

    department: {
      section: 'project',
      get label() {
        return i18n.t('facets.Department', { ns: 'portfolio' });
      },
      field: 'DepartmentId',
      facetField: 'DepartmentName', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'departments',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      includeNullInNot: true,
      featureFlag: 'departments',
    },

    status: {
      section: 'project',
      get label() {
        return i18n.t('facets.Status', { ns: 'portfolio' });
      },
      field: 'StatusName',
      // facetField: 'StatusName', // Count by name (Typesense requires string fields for faceting)
      type: 'string',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: [...PROJECT_STATUSES],
      },
      facetBy: true,
      includeNullInNot: true,
    },

    ['is-active']: {
      section: 'project',
      get label() {
        return i18n.t('facets.Active', { ns: 'portfolio' });
      },
      field: 'IsActive',
      type: 'bool',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: ['true', 'false'],
      },
      facetBy: true,
      includeNullInNot: true,
    },

    type: {
      section: 'project',
      get label() {
        return i18n.t('facets.Type', { ns: 'portfolio' });
      },
      field: 'TypeId',
      facetField: 'TypeName', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'projecttypes',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      includeNullInNot: true,
    },

    category: {
      section: 'project',
      get label() {
        return i18n.t('facets.Category', { ns: 'portfolio' });
      },
      field: 'CategoryId',
      facetField: 'Category', // Get readable counts by name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'projectcategories',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      includeNullInNot: true,
      featureFlag: 'projectCategory',
    },

    internal: {
      section: 'project',
      get label() {
        return i18n.t('facets.InternalProject', { ns: 'portfolio' });
      },
      field: 'IsInternalProject',
      type: 'bool',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: ['true', 'false'],
      },
      facetBy: true,
      includeNullInNot: true,
    },

    ['health-status']: {
      section: 'project',
      get label() {
        return i18n.t('facets.Health', { ns: 'portfolio' });
      },
      field: 'HealthStatusName',
      type: 'string',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: [...HEALTH_STATUSES], // Hardcoded list - matches HealthStatusName values
      },
      facetBy: true,
      includeNullInNot: true,
    },

    ['last-time-registration']: {
      section: 'project',
      get label() {
        return i18n.t('facets.LastTimeRegistration', { ns: 'portfolio' });
      },
      field: 'LastTimeRegistrationDate',
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: ['7-days', '14-days', '30-days', '90-days'],
      },
      facetBy: false, // No counts needed
      includeNullInNot: true,
      singleSelect: true, // Only one period makes sense at a time
      isPeriodBasedFilter: true, // Convert period enums to date range comparisons
    },
  },
} satisfies ResourceConfig;

/**
 * Column sort field mappings
 * Maps display fields to their corresponding Typesense sort fields
 */
export const PORTFOLIO_SORT_FIELD_MAP: Record<string, string> = {
  StatusName: 'StatusSortOrder',
  HealthStatusName: 'HealthStatusSortOrder',
  // Add more mappings as needed
};

/**
 * localStorage keys for Portfolio preferences
 */
export const PORTFOLIO_STORAGE_KEYS = {
  // View mode
  viewMode: 'project-portfolio-view-mode',

  // Table view settings
  tableColumnVisibility: 'project-portfolio-table-columns',
  tableColumnWidths: 'project-portfolio-table-widths',
  tableColumnOrder: 'project-portfolio-table-column-order',
  tableSort: 'project-portfolio-table-sort',
  density: 'project-portfolio-density',

  // Cards view settings
  cardFieldsVisibility: 'project-portfolio-card-fields',
  cardsSort: 'project-portfolio-cards-sort',
} satisfies TablePreferenceStorageKeys;

/**
 * Default column visibility for Portfolio table view
 */
export const defaultTableColumnVisibility: GridColumnVisibilityModel = {
  HealthStatusName: true,
  Number: true,
  Name: true,
  CustomerName: true,
  CustomerNumber: true,
  ProjectManagerId: true,
  Category: false,
  Type: false,
  StatusName: true,
  LegalEntityName: true,
  DepartmentName: true,
  StartDate: true,
  EndDate: true,
  LastTimeRegistrationDate: true,
  PoNumber: true,
  // HoursRegistered: false,
  // TotalTasks: false,
  // OpenTasks: false,
  IsInternalProject: true,
  IsActive: true,
};

/**
 * Default field visibility for Portfolio cards view
 * Note: Uses same type as table columns for consistency
 */
export const defaultCardFieldsVisibility: GridColumnVisibilityModel = {
  Number: true,
  Name: true,
  CustomerName: true,
  ProjectManagerId: true,
  Category: true,
  Type: false,
  StatusName: true,
  hours: false,
  TotalTasks: false,
  OpenTasks: false,
  IsInternalProject: false,
  IsActive: false,
  actions: false, // No actions column in cards
};

/**
 * Column visibility requirements based on features and settings
 * Maps column field names to their visibility requirements
 */
export interface ColumnVisibilityRequirement {
  /** Feature flag from configuration-features endpoint */
  featureFlag?: keyof ConfigurationFeaturesResponse;
}

export const columnVisibilityRequirements: Record<
  string,
  ColumnVisibilityRequirement
> = {
  AccountManagerName: {
    featureFlag: 'accountManager',
  },
  PartnerName: {
    featureFlag: 'partner',
  },
  Category: {
    featureFlag: 'projectCategory',
  },
  DepartmentName: {
    featureFlag: 'departments',
  },
  LegalEntityName: {
    featureFlag: 'multiLegalEntities',
  },
  PoNumber: {
    featureFlag: 'projectPONo',
  },
};
